import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { COMPANY } from "@/config/site";

const links = [
  { to: "/", label: "Accueil", testid: NAV.home, end: true },
  { to: "/services", label: "Services", testid: NAV.services },
  { to: "/realisations", label: "Réalisations", testid: NAV.gallery },
  { to: "/a-propos", label: "À propos", testid: NAV.about },
  { to: "/temoignages", label: "Témoignages", testid: NAV.testimonials },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/10"
          : "bg-white/80 backdrop-blur-md border-b border-black/5"
      }`}
    >
      <div className="container-editorial flex items-center justify-between h-24 md:h-28">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center group -my-2"
            aria-label="PENOW — Accueil"
          >
            <img
              src={COMPANY.logoUrl}
              alt="PENOW Rénovation"
              className="h-16 md:h-20 w-auto object-contain"
              decoding="async"
            />
          </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors ${
                  isActive ? "text-terracotta" : "text-anthracite hover:text-terracotta"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-[1px] bg-terracotta transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <a
            href={COMPANY.phoneHref}
            className="hidden md:inline-flex items-center gap-2 text-sm text-anthracite hover:text-terracotta transition-colors"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden xl:inline">{COMPANY.phone}</span>
          </a>
          <Link
            to="/contact"
            data-testid={NAV.ctaQuote}
            className="hidden md:inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-5 py-2.5 text-sm tracking-wide transition-colors"
          >
            Devis gratuit
          </Link>
          <button
            className="lg:hidden p-2 -mr-2"
            aria-label="Menu"
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid={NAV.mobileDrawer}
        className={`lg:hidden overflow-hidden transition-all duration-300 border-b border-black/10 ${
          open ? "max-h-[500px]" : "max-h-0"
        }`}
      >
        <div className="container-editorial py-6 flex flex-col gap-4 bg-white">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `text-base py-1 border-l-2 pl-3 transition-colors ${
                  isActive ? "border-terracotta text-terracotta" : "border-transparent text-anthracite"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-nav-cta-quote"
            className="mt-2 inline-flex justify-center items-center bg-terracotta text-white px-5 py-3 text-sm tracking-wide"
          >
            Devis gratuit
          </Link>
          <a
            href={COMPANY.phoneHref}
            data-testid="mobile-header-phone"
            className="text-center text-sm text-anthracite/70"
          >
            {COMPANY.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
